import { useState } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import {
  FileText,
  Plus,
  RefreshCw,
  Download,
  Shield,
  CheckCircle,
  AlertTriangle,
  Boxes,
  Scale,
  Leaf,
  Cpu,
  Gauge,
  TrendingUp,
  Link2,
  ListTree,
  Layers,
  GitBranch,
} from 'lucide-react';
import { RequirementForm, RequirementsTable } from '@/components/Requirements';
import { TraceabilityGraph } from '@/components/Requirements/TraceabilityGraph';
import { TraceabilityCoverage } from '@/components/Requirements/TraceabilityCoverage';
import { BaselineManager } from '@/components/Requirements/BaselineManager';
import {
  useRequirements,
  useRequirementsStats,
  useDeleteRequirement,
  useApproveRequirement,
  useENSStats,
  useComplianceFrameworks,
  useTraceabilityCoverage,
} from '@/hooks/useRequirements';
import type { Requirement, RequirementFilters } from '@/types/requirements';
import {
  categoryLabels,
  statusLabels,
  priorityLabels,
  levelLabels,
} from '@/types/requirements';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function RequirementsManagement() {
  const [activeTab, setActiveTab] = useState('requirements');
  const [filters, setFilters] = useState<RequirementFilters>({});
  const [formOpen, setFormOpen] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState<Requirement | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  const { data: requirements, isLoading, refetch } = useRequirements(filters);
  const { data: stats } = useRequirementsStats();
  const { data: ensStats } = useENSStats();
  const { data: frameworks } = useComplianceFrameworks();
  const deleteRequirement = useDeleteRequirement();
  const approveRequirement = useApproveRequirement();

  const handleView = (req: Requirement) => {
    setSelectedRequirement(req);
    setDetailOpen(true);
  };

  const handleEdit = (req: Requirement) => {
    setSelectedRequirement(req);
    setFormOpen(true);
  };

  const handleDelete = (req: Requirement) => {
    setSelectedRequirement(req);
    setDeleteDialogOpen(true);
  };

  const handleApprove = (req: Requirement) => {
    setSelectedRequirement(req);
    setApproveDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedRequirement) {
      await deleteRequirement.mutateAsync(selectedRequirement.id);
      setDeleteDialogOpen(false);
      setSelectedRequirement(null);
    }
  };

  const confirmApprove = async () => {
    if (selectedRequirement) {
      await approveRequirement.mutateAsync({ id: selectedRequirement.id });
      setApproveDialogOpen(false);
      setSelectedRequirement(null);
    }
  };

  const categoryIcon = (cat: string) => {
    switch (cat) {
      case 'functional': return <Boxes className="h-4 w-4" />;
      case 'non_functional': return <Gauge className="h-4 w-4" />;
      case 'technical': return <Cpu className="h-4 w-4" />;
      case 'legal': return <Scale className="h-4 w-4" />;
      case 'environmental': return <Leaf className="h-4 w-4" />;
      case 'security': return <Shield className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="h-7 w-7 text-primary" />
              Gestion de Requisitos
            </h1>
            <p className="text-muted-foreground">
              PECAL 2210 / ISO 29148 / CMMI REQM - Trazabilidad y Compliance
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Exportar RTM
            </Button>
            <Button onClick={() => { setSelectedRequirement(null); setFormOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Requisito
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold">{stats?.total_requirements || 0}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Aprobados</p>
                  <p className="text-2xl font-bold">
                    {stats?.by_status?.approved || 0}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Pendientes</p>
                  <p className="text-2xl font-bold">
                    {(stats?.by_status?.draft || 0) + (stats?.by_status?.proposed || 0)}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Criticos</p>
                  <p className="text-2xl font-bold">
                    {stats?.by_priority?.critical || 0}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Cobertura</p>
                  <p className="text-2xl font-bold">
                    {stats?.coverage_percentage || 0}%
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-cyan-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">ENS</p>
                  <p className="text-2xl font-bold">
                    {ensStats?.coverage_percentage || 0}%
                  </p>
                </div>
                <Shield className="h-8 w-8 text-cyan-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="requirements" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Requisitos
            </TabsTrigger>
            <TabsTrigger value="traceability" className="flex items-center gap-2">
              <GitBranch className="h-4 w-4" />
              Trazabilidad
            </TabsTrigger>
            <TabsTrigger value="baselines" className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Lineas Base
            </TabsTrigger>
            <TabsTrigger value="compliance" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Compliance
            </TabsTrigger>
          </TabsList>

          {/* Tab: Requisitos */}
          <TabsContent value="requirements" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Matriz de Requisitos</CardTitle>
                <CardDescription>
                  Gestion de requisitos segun ISO/IEC/IEEE 29148 con trazabilidad PECAL 2210
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RequirementsTable
                  requirements={requirements || []}
                  isLoading={isLoading}
                  filters={filters}
                  onFiltersChange={setFilters}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onApprove={handleApprove}
                  onLink={() => {}}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Trazabilidad */}
          <TabsContent value="traceability" className="mt-4 space-y-4">
            {/* Grafo de Trazabilidad */}
            <TraceabilityGraph
              onNodeClick={(nodeId) => {
                const req = requirements?.find((r) => r.id === nodeId);
                if (req) handleView(req);
              }}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Cobertura de Trazabilidad */}
              <TraceabilityCoverage className="lg:col-span-2" />

              {/* Distribucion por Nivel */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ListTree className="h-5 w-5" />
                    Distribucion por Nivel
                  </CardTitle>
                  <CardDescription>ISO 29148: STR → SYS → SRS → DES → TST</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(levelLabels).map(([level, label]) => {
                    const count = requirements?.filter(r => r.level === level).length || 0;
                    const total = requirements?.length || 1;
                    const percentage = Math.round((count / total) * 100);
                    return (
                      <div key={level} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>{label}</span>
                          <span className="text-muted-foreground">{count} ({percentage}%)</span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Distribucion por Categoria */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Boxes className="h-5 w-5" />
                  Distribucion por Categoria
                </CardTitle>
                <CardDescription>Clasificacion segun tipo de requisito</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {Object.entries(categoryLabels).map(([cat, label]) => {
                    const count = requirements?.filter(r => r.category === cat).length || 0;
                    const total = requirements?.length || 1;
                    const percentage = Math.round((count / total) * 100);
                    return (
                      <div key={cat} className="p-3 border rounded-lg text-center">
                        <div className="flex justify-center mb-2">
                          {categoryIcon(cat)}
                        </div>
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <Progress value={percentage} className="h-1 mt-2" />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Lineas Base */}
          <TabsContent value="baselines" className="mt-4">
            <BaselineManager
              onViewBaseline={(baseline) => {
                console.log('Ver baseline:', baseline.baseline_id);
              }}
            />
          </TabsContent>

          {/* Tab: Compliance */}
          <TabsContent value="compliance" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Marcos Normativos */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Marcos Normativos Aplicados</CardTitle>
                  <CardDescription>Estandares vinculados a requisitos</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {frameworks?.map((f) => {
                      const count = requirements?.filter(r =>
                        r.compliance_standards?.includes(f.code)
                      ).length || 0;
                      return (
                        <div
                          key={f.code}
                          className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline">{f.code}</Badge>
                            <span className="text-sm font-bold">{count}</span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{f.name}</p>
                          {f.is_mandatory && (
                            <Badge variant="destructive" className="mt-1 text-xs">
                              Obligatorio
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* ENS Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    ENS - Esquema Nacional
                  </CardTitle>
                  <CardDescription>RD 311/2022</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-primary">
                      {ensStats?.coverage_percentage || 0}%
                    </p>
                    <p className="text-sm text-muted-foreground">Cobertura de medidas</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total medidas</span>
                      <span className="font-medium">{ensStats?.total || 73}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Implementadas</span>
                      <span className="font-medium text-green-600">{ensStats?.implemented || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>En progreso</span>
                      <span className="font-medium text-yellow-600">{ensStats?.in_progress || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Verificadas</span>
                      <span className="font-medium text-blue-600">{ensStats?.verified || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Formulario de Requisito */}
        <RequirementForm
          open={formOpen}
          onOpenChange={setFormOpen}
          requirement={selectedRequirement}
          onSuccess={() => {
            refetch();
            setSelectedRequirement(null);
          }}
        />

        {/* Dialogo de Detalle */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {selectedRequirement?.requirement_id}
              </DialogTitle>
              <DialogDescription>{selectedRequirement?.title}</DialogDescription>
            </DialogHeader>
            {selectedRequirement && (
              <ScrollArea className="max-h-[60vh]">
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <Badge>{categoryLabels[selectedRequirement.category]}</Badge>
                    <Badge variant="outline">{levelLabels[selectedRequirement.level]}</Badge>
                    <Badge variant="outline">{statusLabels[selectedRequirement.status]}</Badge>
                    <Badge variant="outline">{priorityLabels[selectedRequirement.priority]}</Badge>
                  </div>

                  <div>
                    <h4 className="font-medium mb-1">Descripcion</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {selectedRequirement.description}
                    </p>
                  </div>

                  {selectedRequirement.rationale && (
                    <div>
                      <h4 className="font-medium mb-1">Justificacion</h4>
                      <p className="text-sm text-muted-foreground">{selectedRequirement.rationale}</p>
                    </div>
                  )}

                  {selectedRequirement.acceptance_criteria && (
                    <div>
                      <h4 className="font-medium mb-1">Criterios de Aceptacion</h4>
                      <p className="text-sm text-muted-foreground">{selectedRequirement.acceptance_criteria}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Creado por:</span>
                      <p>{selectedRequirement.created_by_name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Fecha:</span>
                      <p>{format(new Date(selectedRequirement.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}</p>
                    </div>
                    {selectedRequirement.assigned_to_name && (
                      <div>
                        <span className="text-muted-foreground">Asignado a:</span>
                        <p>{selectedRequirement.assigned_to_name}</p>
                      </div>
                    )}
                    {selectedRequirement.approved_by_name && (
                      <div>
                        <span className="text-muted-foreground">Aprobado por:</span>
                        <p>{selectedRequirement.approved_by_name}</p>
                      </div>
                    )}
                  </div>

                  {selectedRequirement.compliance_standards.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-1">Estandares de Compliance</h4>
                      <div className="flex flex-wrap gap-1">
                        {selectedRequirement.compliance_standards.map((s) => (
                          <Badge key={s} variant="outline">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialogo de Confirmacion Eliminar */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar Requisito</AlertDialogTitle>
              <AlertDialogDescription>
                ¿Esta seguro de eliminar el requisito{' '}
                <strong>{selectedRequirement?.requirement_id}</strong>?
                Esta accion no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Dialogo de Confirmacion Aprobar */}
        <AlertDialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Aprobar Requisito</AlertDialogTitle>
              <AlertDialogDescription>
                ¿Confirma la aprobacion del requisito{' '}
                <strong>{selectedRequirement?.requirement_id}</strong>?
                <br /><br />
                <span className="text-xs text-muted-foreground">
                  Nota: Segun CMMI REQM, el creador del requisito no puede aprobarlo (segregacion de funciones).
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmApprove}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Aprobar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
